import { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";

// Markdown elements we allow to render inside notes. Mirrors the email
// sanitizer's spirit but kept tight to what GitHub-flavored markdown emits.
const ALLOWED_TAGS = [
  "p", "br", "hr", "b", "strong", "i", "em", "u", "s", "del", "mark",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "pre", "code", "a",
  "table", "thead", "tbody", "tr", "td", "th",
  "input", // task-list checkboxes
];
const ALLOWED_ATTR = ["href", "target", "rel", "type", "checked", "disabled", "start"];

marked.setOptions({ gfm: true, breaks: true });

// Open links in a new tab without leaking the opener.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Renders a markdown string as styled, sanitized HTML. Used for free-text
 * notes so headings, lists, tables, code blocks, etc. display properly
 * instead of as raw `##`/`-` characters.
 */
export function Markdown({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const html = useMemo(() => {
    const raw = marked.parse(content ?? "", { async: false }) as string;
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_ATTR: ["target"],
    });
  }, [content]);

  return (
    <div
      className={cn(
        "text-sm leading-relaxed break-words",
        // Headings
        "[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:tracking-tight",
        "[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:tracking-tight",
        "[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold",
        "[&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-sm [&_h4]:font-semibold",
        "[&_h5]:mt-2 [&_h5]:mb-1 [&_h5]:text-sm [&_h5]:font-semibold",
        "[&_h6]:mt-2 [&_h6]:mb-1 [&_h6]:text-xs [&_h6]:font-semibold [&_h6]:uppercase [&_h6]:tracking-wide [&_h6]:text-muted-foreground",
        "[&_:first-child]:mt-0 [&_:last-child]:mb-0",
        // Paragraphs
        "[&_p]:my-2",
        // Lists
        "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1",
        "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1",
        "[&_li]:marker:text-muted-foreground",
        "[&_li>ul]:my-1 [&_li>ol]:my-1",
        // Inline
        "[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
        "[&_strong]:font-semibold",
        // Code
        "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]",
        "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3",
        "[&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_pre>code]:text-xs",
        // Blockquote
        "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground",
        // Rule
        "[&_hr]:my-3 [&_hr]:border-border",
        // Tables
        "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
        "[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold",
        "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
        className
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
